import React, { useRef, useEffect, useState } from 'react';
import { StationSector } from '../../../../../../utils/types';
import { StationFormSection } from './StationFormSection';
import { AircraftDetailsSection } from './AircraftDetailsSection';
import { SectorCodeshareSection } from './SectorCodeshareSection';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface StationSectorCardProps {
  station: StationSector;
  sectorIndex: number;
  flightOwner: string;
  isOpen: boolean;
  canRemove: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onUpdate: (key: keyof StationSector, value: StationSector[keyof StationSector]) => void;
  getFieldError?: (sectorIndex: number, fieldName: keyof StationSector) => string | null;
  handleBlur?: (sectorIndex: number, fieldName: keyof StationSector, value: StationSector[keyof StationSector], station?: StationSector) => void;
  revalidateIfTouched?: (sectorIndex: number, fieldName: keyof StationSector, value: StationSector[keyof StationSector], station?: StationSector) => void;
}

/**
 * Card component for a single station sector (pair of stations + aircraft details)
 * Uses same collapse animation as FormSection
 */
export function StationSectorCard({
  station,
  sectorIndex,
  flightOwner,
  isOpen,
  canRemove,
  onToggle,
  onRemove,
  onUpdate,
  getFieldError,
  handleBlur,
  revalidateIfTouched,
}: StationSectorCardProps) {
  // Calculate sector number: 0-based index becomes 1-based sector number
  const sectorNumber = sectorIndex + 1;

  const [height, setHeight] = useState<number | undefined>(isOpen ? undefined : 0);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    // If currently expanded with auto height, we need to set explicit height first
    if (isOpen && height === undefined && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
      // Then collapse on next frame
      requestAnimationFrame(() => {
        onToggle();
      });
    } else {
      onToggle();
    }
  };

  useEffect(() => {
    if (contentRef.current) {
      if (isOpen) {
        const contentHeight = contentRef.current.scrollHeight;
        setHeight(contentHeight);
        // After animation completes, set to undefined for auto height
        const timer = setTimeout(() => setHeight(undefined), 300);
        return () => clearTimeout(timer);
      } else {
        // When collapsing, first ensure we have actual height, then collapse to 0
        const contentHeight = contentRef.current.scrollHeight;
        setHeight(contentHeight);
        // Use requestAnimationFrame to ensure the height is set before collapsing
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setHeight(0);
          });
        });
      }
    }
  }, [isOpen]);

  return (
    <div ref={cardRef} className="bg-white border border-gray-200 rounded-xl p-4">
      {/* Sector Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="px-3 py-1.5 bg-primary-500 text-white rounded-md text-sm font-bold tracking-wider">
            SECTOR {sectorNumber}
          </span>
          <span className="font-mono text-gray-900 font-medium">
            {station.departureStation || '___'} → {station.arrivalStation || '___'}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleToggle}
            aria-expanded={isOpen}
            className="p-1.5 bg-gray-50 border border-gray-200 rounded-md text-gray-600 cursor-pointer hover:bg-gray-100 transition-colors"
          >
            <div className={`transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            </div>
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={!canRemove}
            className="px-3.5 py-1.5 bg-white border border-danger-200 rounded-md text-danger-600 text-sm font-medium cursor-pointer hover:bg-danger-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Remove
          </button>
        </div>
      </div>

      {/* Collapsible Content */}
      <div
        ref={contentRef}
        style={{
          height: height !== undefined ? `${height}px` : 'auto',
          overflow: 'hidden',
          transition: 'height 300ms ease-in-out',
        }}
      >
        <div className="space-y-4 pt-4">
            {/* Departure */}
            <StationFormSection
              station={station}
              sectorNumber={sectorNumber}
              isDeparture={true}
              sectorIndex={sectorIndex}
              onUpdate={onUpdate}
              getFieldError={getFieldError}
              handleBlur={handleBlur}
              revalidateIfTouched={revalidateIfTouched}
            />

            {/* Arrival */}
            <StationFormSection
              station={station}
              sectorNumber={sectorNumber}
              isDeparture={false}
              sectorIndex={sectorIndex}
              onUpdate={onUpdate}
              getFieldError={getFieldError}
              handleBlur={handleBlur}
              revalidateIfTouched={revalidateIfTouched}
            />

          {/* Aircraft Details */}
          <AircraftDetailsSection
            station={station}
            sectorIndex={sectorIndex}
            flightOwner={flightOwner}
            onUpdate={onUpdate}
            getFieldError={getFieldError}
            handleBlur={handleBlur}
            revalidateIfTouched={revalidateIfTouched}
          />

          {/* Sector-Level Codeshare */}
          <SectorCodeshareSection
            station={station}
            sectorIndex={sectorIndex}
            onUpdate={onUpdate}
          />
        </div>
      </div>
    </div>
  );
}
